import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ShieldCheck, Check, X, Info, Plus, Trash, 
  MagnifyingGlass, Sparkle, FunnelSimple, CaretDown
} from '@phosphor-icons/react';

// All granular permission actions
const PERMISSION_ACTIONS = [
  { id: 'can_view', label: 'View', group: 'basic', color: 'blue' },
  { id: 'can_create', label: 'Create', group: 'basic', color: 'green' },
  { id: 'can_edit_own', label: 'Edit Own', group: 'basic', color: 'yellow' },
  { id: 'can_edit_any', label: 'Edit Any', group: 'basic', color: 'orange' },
  { id: 'can_delete_own', label: 'Delete Own', group: 'basic', color: 'red' },
  { id: 'can_delete_any', label: 'Delete Any', group: 'basic', color: 'red' },
  { id: 'can_submit_for_review', label: 'Submit Review', group: 'publishing', color: 'purple' },
  { id: 'can_publish', label: 'Publish', group: 'publishing', color: 'green' },
  { id: 'can_unpublish', label: 'Unpublish', group: 'publishing', color: 'red' },
  { id: 'can_schedule_publishing', label: 'Schedule', group: 'publishing', color: 'blue' },
  { id: 'can_feature_content', label: 'Feature', group: 'management', color: 'yellow' },
  { id: 'can_archive', label: 'Archive', group: 'management', color: 'gray' },
  { id: 'can_restore', label: 'Restore', group: 'management', color: 'green' },
  { id: 'can_duplicate', label: 'Duplicate', group: 'management', color: 'blue' },
  { id: 'can_export', label: 'Export', group: 'management', color: 'indigo' },
  { id: 'can_manage_seo', label: 'SEO', group: 'advanced', color: 'purple' },
  { id: 'can_upload_media', label: 'Media', group: 'advanced', color: 'pink' },
  { id: 'can_manage_categories', label: 'Categories', group: 'advanced', color: 'cyan' },
  { id: 'can_manage_tags', label: 'Tags', group: 'advanced', color: 'teal' },
];

const DEFAULT_ROLES = [
  { id: 'visitor', label: 'Visitor', color: 'gray' },
  { id: 'contributor', label: 'Contributor', color: 'blue' },
  { id: 'writer', label: 'Writer', color: 'green' },
  { id: 'trusted_writer', label: 'Trusted Writer', color: 'yellow' },
  { id: 'editor', label: 'Editor', color: 'purple' },
  { id: 'admin', label: 'Admin', color: 'red' },
];

export default function CmsContentPermissions() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [viewMode, setViewMode] = useState('matrix'); // 'matrix' or 'detailed'
  const [filterGroup, setFilterGroup] = useState('all'); // 'all', 'basic', 'publishing', 'management', 'advanced'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Custom role creation
  const [showCustomRoleForm, setShowCustomRoleForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleBaseRole, setNewRoleBaseRole] = useState('writer');
  
  // New content type creation
  const [showNewTypeForm, setShowNewTypeForm] = useState(false);
  const [newContentTypeId, setNewContentTypeId] = useState('');
  const [newContentTypeName, setNewContentTypeName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [permsRes, rolesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions/custom-roles/list`, { withCredentials: true })
      ]);
      
      setPermissions(permsRes.data);
      setCustomRoles(rolesRes.data);
      
      if (permsRes.data.length > 0) {
        setSelectedType(permsRes.data[0].content_type);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = async (contentType, role, permission) => {
    try {
      const typePerms = permissions.find(p => p.content_type === contentType);
      if (!typePerms) return;

      const currentValue = typePerms.roles?.[role]?.[permission] || false;
      const newRoles = {
        ...typePerms.roles,
        [role]: {
          ...(typePerms.roles[role] || {}),
          [permission]: !currentValue
        }
      };

      const updatedTypePerms = {
        ...typePerms,
        roles: newRoles
      };

      // Optimistic update
      setPermissions(permissions.map(p => 
        p.content_type === contentType ? updatedTypePerms : p
      ));

      // Save to backend
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions/${contentType}`,
        updatedTypePerms,
        { withCredentials: true }
      );

      toast.success('Permission updated');
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error('Failed to update permission');
      fetchData(); // Revert on error
    }
  };

  const handleUpdateApprovalRules = async (contentType, rules) => {
    try {
      const typePerms = permissions.find(p => p.content_type === contentType);
      if (!typePerms) return;

      const updatedTypePerms = {
        ...typePerms,
        approval_rules: { ...typePerms.approval_rules, ...rules }
      };

      setPermissions(permissions.map(p => 
        p.content_type === contentType ? updatedTypePerms : p
      ));

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions/${contentType}`,
        updatedTypePerms,
        { withCredentials: true }
      );

      toast.success('Approval rules updated');
    } catch (error) {
      console.error('Error updating approval rules:', error);
      toast.error('Failed to update approval rules');
      fetchData();
    }
  };

  const handleCreateCustomRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions/custom-roles/create`,
        {
          role_name: newRoleName,
          base_role: newRoleBaseRole,
          description: `Custom ${newRoleName} role`,
          color: 'indigo'
        },
        { withCredentials: true }
      );

      toast.success('Custom role created');
      setNewRoleName('');
      setShowCustomRoleForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating custom role:', error);
      toast.error(error.response?.data?.detail || 'Failed to create custom role');
    }
  };

  const handleCreateContentType = async (e) => {
    e.preventDefault();
    if (!newContentTypeId.trim() || !newContentTypeName.trim()) {
      toast.error('Both fields are required');
      return;
    }

    try {
      // Create new content type with default permissions
      const defaults = {
        content_type: newContentTypeId,
        display_name: newContentTypeName,
        approval_rules: {
          approval_required: true,
          auto_publish_for_trusted_writers: false,
          auto_publish_for_editors: true,
          allow_scheduled_publishing: true,
          allow_guest_submission: false,
          require_editorial_review: true,
          allow_ai_assistance: true,
          allow_comments: true,
          allow_public_editing: false
        },
        roles: {}
      };

      // Inherit permissions from 'story' as template
      const storyPerms = permissions.find(p => p.content_type === 'story');
      if (storyPerms) {
        defaults.roles = { ...storyPerms.roles };
      }

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content-permissions/${newContentTypeId}`,
        defaults,
        { withCredentials: true }
      );

      toast.success('New content type created');
      setNewContentTypeId('');
      setNewContentTypeName('');
      setShowNewTypeForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating content type:', error);
      toast.error(error.response?.data?.detail || 'Failed to create content type');
    }
  };

  const allRoles = [...DEFAULT_ROLES, ...customRoles.map(r => ({
    id: r.role_id,
    label: r.role_name,
    color: r.color || 'indigo'
  }))];

  const filteredPermissions = PERMISSION_ACTIONS.filter(p => {
    if (filterGroup === 'all') return true;
    return p.group === filterGroup;
  });

  const filteredContentTypes = permissions.filter(p => 
    p.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.content_type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <ShieldCheck size={32} weight="fill" className="text-primary" />
            Content Permission Matrix
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure granular permissions for all content types and roles
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowNewTypeForm(!showNewTypeForm)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus size={16} weight="bold" />
            New Content Type
          </button>
          <button
            onClick={() => setShowCustomRoleForm(!showCustomRoleForm)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus size={16} weight="bold" />
            Custom Role
          </button>
        </div>
      </div>

      {/* Custom Role Creation Form */}
      {showCustomRoleForm && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Create Custom Role</h3>
          <form onSubmit={handleCreateCustomRole} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Role Name</label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Poetry Editor, Junior Reviewer"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Inherit From</label>
                <select
                  value={newRoleBaseRole}
                  onChange={(e) => setNewRoleBaseRole(e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                >
                  {DEFAULT_ROLES.filter(r => r.id !== 'visitor').map(role => (
                    <option key={role.id} value={role.id}>{role.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Create Role
              </button>
              <button
                type="button"
                onClick={() => setShowCustomRoleForm(false)}
                className="px-6 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* New Content Type Creation Form */}
      {showNewTypeForm && (
        <div className="bg-card border border-green-500/30 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plus size={20} className="text-green-500" />
            Create New Content Type
          </h3>
          <form onSubmit={handleCreateContentType} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Content Type ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newContentTypeId}
                  onChange={(e) => setNewContentTypeId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  placeholder="e.g., biography, comic, screenplay"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lowercase, underscores only (auto-formatted)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newContentTypeName}
                  onChange={(e) => setNewContentTypeName(e.target.value)}
                  placeholder="e.g., Biography, Comic Book, Screenplay"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                />
              </div>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                <Info size={16} className="inline mr-1" />
                New content type will inherit default permissions from the "Story" template. 
                You can customize permissions after creation.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Create Content Type
              </button>
              <button
                type="button"
                onClick={() => setShowNewTypeForm(false)}
                className="px-6 py-2 border border-border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search content types..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <FunnelSimple size={20} className="text-muted-foreground" />
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="px-4 py-2 bg-background border border-border rounded-lg"
            >
              <option value="all">All Permissions</option>
              <option value="basic">Basic Actions</option>
              <option value="publishing">Publishing</option>
              <option value="management">Management</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div className="flex border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('matrix')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === 'matrix' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              Matrix View
            </button>
            <button
              onClick={() => setViewMode('detailed')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                viewMode === 'detailed' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
            >
              Detailed View
            </button>
          </div>
        </div>
      </div>

      {/* Permission Matrix */}
      {viewMode === 'matrix' ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold sticky left-0 bg-muted/50 z-10 min-w-[200px]">
                    Content Type
                  </th>
                  {allRoles.map(role => (
                    <th key={role.id} className="px-4 py-3 text-center text-sm font-semibold min-w-[120px]">
                      <div className={`inline-block px-3 py-1 rounded-full text-xs bg-${role.color}-500/10 text-${role.color}-600 dark:text-${role.color}-400`}>
                        {role.label}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredContentTypes.map(typePerms => (
                  <tr key={typePerms.content_type} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium sticky left-0 bg-card">
                      <button
                        onClick={() => setSelectedType(typePerms.content_type)}
                        className="text-left hover:text-primary transition-colors"
                      >
                        {typePerms.display_name || typePerms.content_type}
                      </button>
                    </td>
                    {allRoles.map(role => {
                      const rolePerms = typePerms.roles?.[role.id] || {};
                      const enabledCount = filteredPermissions.filter(p => rolePerms[p.id]).length;
                      const totalCount = filteredPermissions.length;
                      
                      return (
                        <td key={role.id} className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-xs text-muted-foreground">
                              {enabledCount} / {totalCount}
                            </div>
                            <div className="w-full bg-muted rounded-full h-1.5">
                              <div
                                className={`bg-${role.color}-500 h-1.5 rounded-full transition-all`}
                                style={{ width: `${(enabledCount / totalCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Detailed View */
        <div className="space-y-4">
          {filteredContentTypes.map(typePerms => (
            <details key={typePerms.content_type} className="bg-card border border-border rounded-lg overflow-hidden" open={typePerms.content_type === selectedType}>
              <summary className="px-6 py-4 cursor-pointer hover:bg-muted/50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CaretDown size={20} weight="bold" className="text-muted-foreground" />
                  <h3 className="text-lg font-semibold">{typePerms.display_name || typePerms.content_type}</h3>
                </div>
                <span className="text-sm text-muted-foreground">
                  {Object.keys(typePerms.roles || {}).length} roles configured
                </span>
              </summary>
              
              <div className="p-6 pt-2 space-y-6 border-t border-border">
                {/* Approval Rules */}
                <div className="bg-muted/30 rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-yellow-500" />
                    Approval Rules
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: 'approval_required', label: 'Requires Approval' },
                      { key: 'auto_publish_for_trusted_writers', label: 'Auto-publish (Trusted)' },
                      { key: 'auto_publish_for_editors', label: 'Auto-publish (Editors)' },
                      { key: 'allow_scheduled_publishing', label: 'Allow Scheduling' },
                      { key: 'allow_ai_assistance', label: 'Allow AI Assist' },
                      { key: 'allow_comments', label: 'Allow Comments' },
                    ].map(rule => (
                      <label key={rule.key} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={typePerms.approval_rules?.[rule.key] || false}
                          onChange={(e) => handleUpdateApprovalRules(typePerms.content_type, { [rule.key]: e.target.checked })}
                          className="w-4 h-4 rounded border-border"
                        />
                        {rule.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Permission Grid */}
                <div className="space-y-4">
                  {allRoles.map(role => (
                    <div key={role.id} className="border border-border rounded-lg p-4">
                      <h5 className={`text-sm font-semibold mb-3 text-${role.color}-600 dark:text-${role.color}-400`}>
                        {role.label} Permissions
                      </h5>
                      <div className="grid grid-cols-4 gap-2">
                        {filteredPermissions.map(perm => {
                          const isEnabled = typePerms.roles?.[role.id]?.[perm.id] || false;
                          return (
                            <button
                              key={perm.id}
                              onClick={() => handleTogglePermission(typePerms.content_type, role.id, perm.id)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                isEnabled
                                  ? `bg-${perm.color}-500/20 text-${perm.color}-600 dark:text-${perm.color}-400 border border-${perm.color}-500/30`
                                  : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                              }`}
                            >
                              {isEnabled ? <Check size={12} weight="bold" className="inline mr-1" /> : null}
                              {perm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
        <Info size={20} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-600 dark:text-blue-400">
          <p className="font-medium mb-1">Automatic Content Type Detection</p>
          <p className="text-blue-600/80 dark:text-blue-400/80">
            When new content types are added to the system, they will automatically appear here with default permissions. 
            Administrators can then configure permissions without any code changes.
          </p>
        </div>
      </div>
    </div>
  );
}
